import { Controller } from "@hotwired/stimulus"

// The feedback modal's one piece of conditional logic: a data report names
// the food or recipe it is about, and the other two kinds have nothing to
// name.
//
// The pickers are rendered and hidden rather than fetched on demand — both
// are a <select> plus a combobox, they cost nothing to render, and swapping
// them over a network round trip would make the form feel slower than the
// choice it is responding to.
export default class extends Controller {
  static targets = [
    "kind", "subject", "subjectType", "bodyLabel", "prefillNotice",
    "foodPicker", "foodSelect",
    "recipePicker", "recipeSelect"
  ]

  // WHAT THE PAGE THEY CAME FROM WAS SHOWING. Restored whenever they switch
  // back to that kind, so changing to Food and back to Recipe returns the
  // recipe rather than an empty box they have to search again.
  static values = {
    prefillType: String,
    prefillId: String,
    prefillLabel: String
  }

  // THE QUESTION CHANGES WITH THE KIND. "What happened?" is a bug question:
  // it makes no sense of a macro that looks wrong, and none at all of an
  // idea somebody has had.
  static PROMPTS = {
    idea: "What would you like it to do?",
    bug: "What were you doing when it broke?",
    data: "What should it say instead?"
  }

  connect() {
    this.kindChanged()
  }

  kindChanged() {
    const checked = this.kindTargets.find((r) => r.checked)
    const kind = checked ? checked.value : "idea"
    const data = kind === "data"

    if (this.hasBodyLabelTarget) {
      this.bodyLabelTarget.textContent =
        this.constructor.PROMPTS[kind] || this.constructor.PROMPTS.bug
    }

    this.subjectTarget.classList.toggle("hidden", !data)

    // BOTH SELECTS SHARE A NAME, so exactly one must be enabled at a time —
    // otherwise the browser sends subject_id twice and the server takes
    // whichever came last, which is not necessarily the one on screen.
    if (!data) {
      this.foodSelectTarget.disabled = true
      this.recipeSelectTarget.disabled = true
      return
    }

    this.subjectTypeChanged()
  }

  subjectTypeChanged() {
    // subjectType is a RADIO GROUP, not a select — reading .value off the
    // target gives the first radio's value whatever is checked, so the
    // picker never switched. Ask which one is checked.
    const checked = this.subjectTypeTargets.find((r) => r.checked)
    const food = !checked || checked.value === "Food"

    this.foodPickerTarget.classList.toggle("hidden", !food)
    this.recipePickerTarget.classList.toggle("hidden", food)

    this.foodSelectTarget.disabled = !food
    this.recipeSelectTarget.disabled = food

    // A CHOICE MADE UNDER THE OTHER HEADING IS NOT AN ANSWER TO THIS ONE.
    // Picking a food, switching to Recipe and submitting would otherwise
    // send the food's id under subject_type "Recipe" — a report pointing at
    // the wrong record entirely, and the validation cannot catch it because
    // both ids are real.
    this.clear(food ? this.recipeSelectTarget : this.foodSelectTarget)

    this.restorePrefill(food ? "Food" : "Recipe")
    this.syncPrefillNotice()
  }

  // Puts the pre-filled record back when its own kind is chosen again —
  // including the first time, and after a detour through the other one.
  // Overriding it is just picking something else.
  restorePrefill(kind) {
    if (this.prefillTypeValue !== kind || !this.prefillIdValue) return

    const select = kind === "Food" ? this.foodSelectTarget : this.recipeSelectTarget
    if (select.value) return  // they have chosen something; leave it alone

    if (!Array.from(select.options).some((o) => o.value === this.prefillIdValue)) {
      select.add(new Option(this.prefillLabelValue, this.prefillIdValue))
    }
    select.value = this.prefillIdValue

    const input = select.parentElement?.querySelector('[data-combobox-target="input"]')
    if (input) input.value = this.prefillLabelValue
  }

  // The notice claims a pre-fill, so it may only show while that pre-fill is
  // what is actually selected.
  syncPrefillNotice() {
    if (!this.hasPrefillNoticeTarget) return

    const select = this.prefillTypeValue === "Food"
      ? this.foodSelectTarget
      : this.recipeSelectTarget

    const showing = !select.disabled && select.value === this.prefillIdValue
    this.prefillNoticeTarget.classList.toggle("hidden", !showing)
  }

  // Empties a picker: the hidden select that carries the value, and the text
  // input beside it that shows what was chosen.
  clear(select) {
    select.value = ""

    const input = select.parentElement?.querySelector('[data-combobox-target="input"]')
    if (input) input.value = ""
  }
}
